import React, { CSSProperties, Fragment, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { JSX } from 'react/jsx-runtime';
import { DragEndEvent, DragMoveEvent } from '@dnd-kit/core';
import classNames from 'classnames';
import { Dayjs } from 'dayjs';

import { WeeklyPlannerDatasetOptions as ViewOptions } from '~constants/maps';

import {
  Appointment,
  Break,
  calcIndexFromTime,
  Client,
  Conflict,
  Event,
  getGridColumn,
  stringifyCompare,
  Travel,
  Worker,
  WorkerAvailability,
} from '~weekly-planner/lib/common';
import { MAX_PIPS, onDailyAppointmentDragEnd, onDailyAppointmentDragging } from '~weekly-planner/lib/drag-drop';

import { DraggableItem, DroppableArea } from '~components/drag-and-drop';
import { AppointmentCard, BreakCard, TravelCard } from '~weekly-planner/components/card';
import AppointmentPopovers from '~weekly-planner/components/card/AppointmentPopovers';

import { clearSingleRow as clearVisualisedRow } from '~weekly-planner/reducers/visualise';

import {
  selectFocusedAppointment,
  selectOpenAppointmentDetailsId,
  selectOpenAppointmentRosteringNotesId,
  selectOptions,
  selectPlannerAPIOptions,
  selectViewType,
} from '~weekly-planner/selectors';
import { selectAll as selectUnallocated } from '~weekly-planner/selectors/appointments/unallocated';
import { selectAllSimulatedChanges } from '~weekly-planner/selectors/simulate';

interface ComponentProps {
  date: Dayjs;
  data: Worker | Client;
  events: Event[];
  style?: CSSProperties;
  getAppointmentTitle: (appointment: Appointment) => string;
  startDate?: Dayjs;
  isOnLeave?: boolean;
}

type MarkedAppointment = Appointment & {
  isConflict?: boolean;
};

const Cell: React.FC<ComponentProps> = ({ date, data, events, style, startDate, getAppointmentTitle, isOnLeave }) => {
  const dispatch = useAppDispatch();

  const [isDragging, setIsDragging] = useState(false);
  const [dragContainerStyle, setDragContainerStyle] = useState<CSSProperties>({});
  const [dragAppointment, setDragAppointment] = useState<Appointment | null>(null);
  const [isOver, setIsOver] = useState(false);
  const [isOrigin, setIsOrigin] = useState(false);
  const [overrideValues, setOverrideValues] = useState<any>({});

  const rows = useAppSelector(selectAllSimulatedChanges);
  const unallocated = useAppSelector(selectUnallocated);
  const plannerAPIOptions = useAppSelector(selectPlannerAPIOptions);
  const { showBreaks, showTravel, showTeaBreaks } = useAppSelector(selectOptions);
  const focused = useAppSelector(selectFocusedAppointment);
  const { appointment: focusedAppointment } = focused || {};
  const { key: focusKey } = focusedAppointment || {};
  const { id } = data;
  const key = `${id}-cell`;
  const viewType = useAppSelector(selectViewType);
  const detailsId = useAppSelector(selectOpenAppointmentDetailsId);
  const rosteringNotesId = useAppSelector(selectOpenAppointmentRosteringNotesId);

  const handleDragOverChange = (isOver: boolean, current?: any) => {
    // DO NOT store "current" into state, this will cause maximum depth rendering when any cell is dragged.
    const { appointment, containerStyle } = current ?? {};
    if (!stringifyCompare(containerStyle, dragContainerStyle)) setDragContainerStyle(containerStyle);
    if (!stringifyCompare(appointment, dragAppointment as object)) setDragAppointment(appointment);
    setIsDragging(current !== undefined);
    setIsOver(isOver);
    setIsOrigin(current?.containerId === id);
  };

  const renderAppointments = (cards: MarkedAppointment[], index: string) => (
    <Fragment key={index}>
      {cards.map((appointment: MarkedAppointment, index: number) => {
        let style: CSSProperties = {};

        // Destructure appointment props
        const { is_overnight, is_second_day, start_time, end_time } = appointment;

        // Handle overnight split logic
        let start: string, end: string;
        if (is_overnight) {
          if (is_second_day === false) {
            // First half: start_time -> 23:59
            start = start_time;
            end = '23:59';
          } else {
            // Second half: 00:00 -> end_time
            start = '00:00';
            end = end_time;
          }
        } else {
          start = start_time;
          end = end_time;
        }
        const startIndex = calcIndexFromTime(start) + 1;
        const endIndex = calcIndexFromTime(end) + 1;

        style = {
          gridColumn: `${startIndex} / span ${endIndex - startIndex}`,
          ...(appointment.isConflict ? {} : { gridRow: 1 }),
        };

        const { key, is_cancelled, is_shift_pay, isConflict = false } = appointment;
        const className = classNames('', { conflict: isConflict });

        const cardKey = `${id}-${index}`;
        const contextTarget = `appointment-card-${key}${is_second_day ? '-second-day' : ''}`;

        const props = {
          appointment,
          events,
          overrideValues: appointment.key === dragAppointment?.key ? overrideValues : undefined,
          contextTarget,
        };

        const isDraggable =
          !is_cancelled &&
          !!key &&
          !is_shift_pay &&
          viewType !== ViewOptions.CLIENT &&
          (!appointment.is_overnight || (appointment.is_overnight && appointment.is_second_day === false));

        if (isDraggable) {
          return (
            <>
              <DraggableItem
                key={cardKey}
                id={key}
                style={style}
                renderChildrenWhileDragging={false}
                data={{
                  appointment,
                  containerId: id,
                  containerStyle: style,
                  onItemDragEnd: (event: DragEndEvent) => {
                    setOverrideValues({});
                    dispatch(clearVisualisedRow(appointment.user_id));
                    return onDailyAppointmentDragEnd(event, plannerAPIOptions);
                  },
                  onItemDragging: (event: DragMoveEvent) => {
                    setOverrideValues(() => onDailyAppointmentDragging(event));
                  },
                  data: rows,
                  startDate,
                  dispatch,
                }}
              >
                <AppointmentCard {...props} className={className} style={style} getTitle={getAppointmentTitle} />
              </DraggableItem>
              <AppointmentPopovers
                key={`${cardKey}-popovers`}
                appointment={appointment}
                isDragging={isDragging}
                openDetailsId={detailsId}
                openRosteringNotesId={rosteringNotesId}
                contextTarget={contextTarget}
              />
            </>
          );
        }
        return (
          <>
            <AppointmentCard
              key={`${cardKey}-card`}
              {...props}
              className={className}
              style={{ ...style, gridRow: '1' }}
              getTitle={getAppointmentTitle}
            />
            <AppointmentPopovers
              key={`${cardKey}-popovers`}
              isDragging={isDragging}
              appointment={appointment}
              openDetailsId={detailsId}
              openRosteringNotesId={rosteringNotesId}
              contextTarget={contextTarget}
            />
          </>
        );
      })}
    </Fragment>
  );

  const renderAvailability = (date: Dayjs, isOnLeave: boolean) => {
    const dayOfWeek = date.format('dddd');

    if (viewType === ViewOptions.CLIENT) {
      return <div className="availability" style={{ gridColumn: `1 / span ${MAX_PIPS}`, gridRow: 1 }}></div>;
    }

    if (!('availability' in data) || !data.availability || isOnLeave) return null;

    const shifts: (JSX.Element | null)[] = [];
    const availability = data?.availability?.[dayOfWeek as keyof WorkerAvailability] ?? {};

    const renderSection = (start: string, end: string) => {
      if (!start || !end) return null;

      // handle edge cases for 00:00 / 24:00 start & end
      let startIndex = start === '0000' || start === '000' || start === '2400' ? 1 : calcIndexFromTime(start);
      let endIndex = end === '0000' || end === '000' || end === '2400' ? MAX_PIPS : calcIndexFromTime(end);

      // same start & end time (full availability)
      if (start === end) {
        startIndex = 1;
        endIndex = MAX_PIPS;
      }

      const style: CSSProperties = { gridColumn: `${startIndex + 1} / span ${endIndex - startIndex}`, gridRow: 1 };
      return <div key={`${startIndex}-${endIndex}`} className="availability" style={style}></div>;
    };

    if (Array.isArray(availability)) {
      availability.forEach((shift) => {
        shifts.push(renderSection(shift.start, shift.end));
      });
    } else if (availability.start && availability.end) {
      shifts.push(renderSection(availability.start, availability.end));
    }

    return shifts;
  };

  const renderTravels = (cards: (Travel & { isConflict?: boolean })[], index: string) => (
    <Fragment key={index}>
      {cards
        .map((travel, index: number) => {
          // Don't render travel cards with no defined start/end times.
          const {
            from: { time: start },
            to: { time: end },
          } = travel;
          if (!start || !end) return false;

          const style: CSSProperties = {
            gridRow: travel.isConflict ? 1 + index : 1,
            ...getGridColumn(start, end, 1),
          };

          return <TravelCard key={index} travel={travel} style={style} />;
        })
        .filter(Boolean)}
    </Fragment>
  );

  const renderBreaks = (cards: (Break & { isConflict?: boolean })[], index: string) => (
    <Fragment key={index}>
      {cards
        .map((breakData, index: number) => {
          // Don't render travel cards with no defined start/end times.
          const {
            from: { time: start },
            to: { time: end },
          } = breakData;
          if (!start || !end) return false;

          const style: CSSProperties = {
            gridRow: breakData.isConflict ? 1 + index : 1,
            ...getGridColumn(start, end, 1),
          };

          return <BreakCard key={index} data={breakData} style={style} />;
        })
        .filter(Boolean)}
    </Fragment>
  );

  const renderConflict = (data: Conflict[], index: number) => {
    // Group all appointments together and render
    // Group all travels together and render a range travel
    const appointments: any[] = [];
    const travels: any[] = [];
    const breaks: any[] = [];
    const teaBreaks: any[] = [];
    data.forEach((events) =>
      events.forEach(({ type, data }: Event) => {
        switch (type) {
          case 'appointment': {
            appointments.push({ ...data, isConflict: true });
            break;
          }
          case 'travel': {
            travels.push({ ...data, isConflict: true });
            break;
          }
          case 'break': {
            breaks.push({ ...data, isConflict: true });
            break;
          }
          case 'tea_break': {
            teaBreaks.push({ ...data, isConflict: true });
            break;
          }
          default: {
            break;
          }
        }
      }),
    );

    return (
      <Fragment key={index}>
        {renderAppointments(appointments, `appointment-${index}`)}
        {showTravel && renderTravels(travels, `travel-${index}`)}
        {showBreaks && renderBreaks(breaks, `break-${index}`)}
        {showTeaBreaks && renderBreaks(teaBreaks, `tea-break-${index}`)}
      </Fragment>
    );
  };

  const renderEvents = (events: Event[]) =>
    events
      .map(({ type, data }, index) => {
        switch (type) {
          case 'appointment': {
            return renderAppointments([{ ...data, isConflict: false }], `${type}-${index}`);
          }
          case 'conflict': {
            return renderConflict(data, index);
          }
          case 'travel': {
            return showTravel && renderTravels([data], `${type}-${index}`);
          }
          case 'break': {
            return showBreaks && renderBreaks([data], `${type}-${index}`);
          }
          case 'tea_break': {
            return showTeaBreaks && renderBreaks([data], `${type}-${index}`);
          }
          default: {
            return false;
          }
        }
      })
      .filter(Boolean);

  const renderOriginPlaceholder = () => {
    if (isDragging && isOrigin && dragAppointment && dragContainerStyle) {
      return (
        <div
          key={`${dragAppointment.key}-origin-placeholder`}
          className="draggable-item-placeholder"
          style={{ ...dragContainerStyle, gridRow: '1' }}
        />
      );
    }
    return null;
  };

  const renderDragPlaceholders = () => {
    if (!dragAppointment) return <></>;

    // Show ghost appointment card if dragging over this row
    const showGhostAppointment = isDragging && isOver;

    let style = { ...dragContainerStyle, gridRow: '1' };
    const { start_time, end_time } = overrideValues || {};
    if (isOver && isOrigin && start_time && end_time) {
      style = {
        gridRow: '1',
        ...getGridColumn(start_time, end_time, 1),
      };
    }

    return (
      <>
        {renderDragPlaceholder(dragContainerStyle)}
        {showGhostAppointment && (
          <AppointmentCard
            key={`${key}-dragging`}
            className="dragging ghost"
            contextTarget="dragging-ghost"
            appointment={dragAppointment}
            overrideValues={overrideValues}
            getTitle={getAppointmentTitle}
            style={style}
            events={events}
          />
        )}
      </>
    );
  };

  const renderDragPlaceholder = (style: CSSProperties) => (
    <div key={`${key}-placeholder`} className="drag-placeholder" style={{ ...style, gridRow: '1 / span 5' }} />
  );

  const renderFocusedPlaceholder = () => {
    let start_time, end_time;
    if (focusedAppointment && focusKey) {
      ({ start_time, end_time } = focusedAppointment);
    } else if (focusKey) {
      const foundUnallocated = unallocated.find(({ key }) => key === focusKey) ?? null;
      ({ start_time, end_time } = foundUnallocated || {});
    }

    if (!start_time || !end_time) return <></>;

    const style = getGridColumn(start_time, end_time, 1);
    return renderDragPlaceholder(style);
  };

  const className = classNames('daily-cell', {
    'is-dragging-over': isOver,
    'is-dragging-origin': isOrigin,
    'on-leave': isOnLeave,
  });

  return (
    <DroppableArea key={key} id={id} className={className} style={style} onDragOverChange={handleDragOverChange}>
      {renderAvailability(date, isOnLeave ?? false)}
      {isDragging && renderOriginPlaceholder()}
      {renderEvents(events ?? [])}
      {isDragging && renderDragPlaceholders()}
      {focusKey !== null && renderFocusedPlaceholder()}
    </DroppableArea>
  );
};

export default Cell;
